/**
 * TrialUpgradeFab — floating upgrade pill, plan-aware.
 *
 * Reads the user's `plan_intent` so a Pro trialist is nudged to Pro, Pro+
 * trialist to Pro+, etc. Never cross-sells away from the plan they actually
 * signed up to trial.
 *
 * Clicking the pill goes straight to Stripe Checkout for the trialist's plan
 * (monthly) — no settings detour. Trial users have no Stripe subscription yet,
 * so this always takes the new-subscriber checkout path.
 *
 * Render rules:
 *   - only on active trial or expired_soft_locked
 *   - never for paid users
 *   - hidden if Stripe isn't configured (no publishable key)
 *
 * Positioning:
 *   - Desktop (lg+): fixed bottom-right, stacked ABOVE the Support widget
 *     (bottom-24 ≈ 96px clears a ~64px Support pill + margin).
 *   - Mobile/tablet: fixed bottom-right above the bottom nav (nav is h-16 +
 *     safe-area). Compact "Go full" pill to save screen real estate.
 */

import React, { useMemo, useState } from 'react';
import { ArrowUpRight, Loader2, Sparkles } from 'lucide-react';
import { useTrial } from '../../contexts/TrialContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/useToast';
import { subscriptionService } from '../../services/subscriptionService';
import { trackFunnelEvent } from '../../utils/analytics';

const PLAN_LABEL: Record<string, string> = {
  basic: 'Basic',
  pro: 'Pro',
  pro_plus: 'Pro+',
  agency: 'Agency',
};

const PLAN_PRICE: Record<string, string> = {
  basic: '£9.95/mo',
  pro: '£19.95/mo',
  pro_plus: '£34.95/mo',
  agency: '£69.99/mo',
};

const TrialUpgradeFab: React.FC = () => {
  const { trial } = useTrial();
  const { user } = useAuth();
  const { error } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const stripeConfigured = useMemo(() => {
    const stripeKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
    return !!stripeKey && stripeKey.length > 0;
  }, []);

  if (!trial) return null;
  if (trial.hasPaidAccess) return null;
  if (trial.state !== 'active' && trial.state !== 'expired_soft_locked') return null;
  if (!stripeConfigured) return null;

  // Fallback chain: explicit plan_intent → current plan_name → tier → 'pro'
  // Basic-trial users with no intent recorded should still see "Upgrade to Basic",
  // not be cross-sold to Pro.
  const sub: any = user?.subscription || {};
  const intentRaw =
    sub.plan_intent ||
    sub.planIntent ||
    sub.plan_name ||
    sub.planName ||
    sub.tier ||
    'pro';
  const normalised = String(intentRaw).toLowerCase();
  const validIntent = ['basic', 'pro', 'pro_plus', 'agency'].includes(normalised) ? normalised : 'pro';
  const planLabel = PLAN_LABEL[validIntent] || 'Pro';
  const planPrice = PLAN_PRICE[validIntent] || '';

  // When the intent matches the plan the user is already trialling, "Upgrade to X"
  // is dishonest — they'd be keeping the same plan. It was labelled "Keep X", which
  // walk testers read as ambiguous (keep what? WB-18), with "· £9.95/mo after
  // trial". Both were wrong about what the button does: it opens Stripe Checkout
  // for a new subscription that bills from checkout (subscriptionService
  // createCheckoutSession: "pay immediately (no trial)"). So it now says
  // "Subscribe to X" and drops "after trial".
  const currentTierRaw = String(sub.tier || sub.plan_name || sub.planName || '').toLowerCase();
  const currentTier = currentTierRaw === 'business' ? 'agency' : currentTierRaw;
  const isKeepingCurrentPlan = currentTier === validIntent;

  const isUrgent = trial.state === 'expired_soft_locked' || (trial.daysRemaining ?? 99) <= 1;

  const urgentGradient = 'bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700';
  const calmGradient = 'bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800';
  const gradient = isUrgent ? urgentGradient : calmGradient;

  const handleUpgrade = async () => {
    if (isLoading) return;
    // Fire the funnel beacon BEFORE the Stripe redirect. fetch+keepalive
    // survives `window.location.href = ...` so the event lands even though
    // upgradePlan synchronously navigates us away on success.
    trackFunnelEvent('begin_checkout', '/app', `${validIntent}:fab`);
    setIsLoading(true);
    try {
      // The reason is the server's own words (e.g. a past_due card: "update your card in Billing").
      const redirected = await subscriptionService.upgradePlan(validIntent, 'month', (message) => error(message));
      if (!redirected) {
        setIsLoading(false);
      }
      // On success the browser is already navigating to Stripe; don't clear loading.
    } catch (err) {
      error('Couldn\'t start checkout. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Desktop (lg+): full pill with day countdown + price */}
      <button
        type="button"
        onClick={handleUpgrade}
        disabled={isLoading}
        aria-label={isKeepingCurrentPlan ? `Subscribe to ${planLabel}` : `Upgrade to ${planLabel}`}
        className={[
          'hidden lg:inline-flex items-center gap-2',
          'fixed z-40 right-6 bottom-24',
          'rounded-full pl-4 pr-5 py-3 font-semibold text-white shadow-lg shadow-purple-900/40',
          'transition-transform hover:scale-[1.03] active:scale-[0.98]',
          'disabled:opacity-80 disabled:cursor-wait',
          gradient,
        ].join(' ')}
      >
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/15">
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
          ) : (
            <Sparkles className="w-4 h-4" aria-hidden="true" />
          )}
        </span>
        <span className="flex flex-col leading-tight text-left">
          <span className="text-xs opacity-80">
            {trial.state === 'expired_soft_locked'
              ? 'Trial ended'
              : `${trial.daysRemaining ?? 0} day${(trial.daysRemaining ?? 0) === 1 ? '' : 's'} left`}
          </span>
          <span className="text-sm">
            {isLoading ? 'Opening Stripe…' : (
              <>
                {isKeepingCurrentPlan ? `Subscribe to ${planLabel}` : `Upgrade to ${planLabel}`}
                {planPrice ? (
                  <span className="opacity-80 font-normal">
                    {' · '}{planPrice}
                  </span>
                ) : null}
              </>
            )}
          </span>
        </span>
        {!isLoading && <ArrowUpRight className="w-4 h-4 opacity-90" aria-hidden="true" />}
      </button>

      {/* Mobile/tablet: compact "Go full" pill, above bottom nav */}
      <button
        type="button"
        onClick={handleUpgrade}
        disabled={isLoading}
        aria-label={isKeepingCurrentPlan ? `Subscribe to ${planLabel}` : `Go full — upgrade to ${planLabel}`}
        className={[
          'lg:hidden inline-flex items-center gap-1.5',
          // bottom-20 clears bottom nav (h-16) + safe-area inset
          'fixed z-40 right-4 bottom-20',
          'rounded-full px-3.5 py-2 text-xs font-semibold text-white shadow-lg shadow-purple-900/40',
          'transition-transform active:scale-[0.97]',
          'disabled:opacity-80 disabled:cursor-wait',
          gradient,
        ].join(' ')}
      >
        {isLoading ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
        ) : (
          <Sparkles className="w-3.5 h-3.5" aria-hidden="true" />
        )}
        <span>
          {isLoading ? 'Opening…' : (isKeepingCurrentPlan ? `Subscribe to ${planLabel}` : `Go full · ${planLabel}`)}
        </span>
      </button>
    </>
  );
};

export default TrialUpgradeFab;
